import { useContext, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconPlus,
  IconExchange,
  IconMultipleUsersFilled,
  IconMultipleUsersOutline,
  IconUserCog,
} from '@iwoca/icons';
import { Button, Pill } from '@iwoca/orion';
import classnames from 'classnames';

import { HelpModal } from './components/TelephoneNavItem/HelpModal';
import { baseLinks, secondaryLinks, TMenuLink } from './items/items';
import { NameTag } from './NameTag/NameTag';
import styles from './SellerNavBar.module.css';
import { ActivationContext } from '../../activation/Activation';
import {
  useGetContext,
  useGetSharedFunctionality,
} from '../../api/lending/lapiHooks';
import { buildQueryString } from '../../Buyer/utils/queryParams';
import { useSafeNavigate } from '../../hooks/useSafeNavigate';
import { useUserGroups } from '../../hooks/useUserGroups';
import { ValidRoutePaths } from '../../routing/utils';
import { useGetSellerSpendingLimitRequest } from '../../Seller/SpendingLimitRequests/hooks/useGetSellerSpendingLimitRequest.hook';
import { SafeLink } from '../SafeLink/SafeLink';
import iwocaPayLogo from '../svg/iwocaPayLogo.svg';

export type TMenuIcon = {
  normal: React.ReactNode;
  selected: React.ReactNode;
};

type TExternalLink = {
  href: string;
  newTab: boolean;
};

export const CreatePayLinkButton = () => {
  const { navigate } = useSafeNavigate();
  return (
    <Button
      className={styles.createPayLink}
      onClick={() =>
        navigate({ to: '/pay/pay-links/universal', keepParams: true })
      }
    >
      Create Pay Link
      <IconPlus className={styles.createPayLinkIcon} />
    </Button>
  );
};

export const SellerNavBar = ({ hideSideBar }: { hideSideBar: () => void }) => {
  const {
    context: { isLoggedIn },
  } = useGetContext();
  const { isOnboarded } = useContext(ActivationContext);
  const { navigate } = useSafeNavigate();
  const { isOwner, isStaff, isAdmin, isContributor } = useUserGroups();
  const { spendingLimitRequests } = useGetSellerSpendingLimitRequest();
  const [helpModalVisible, setHelpModalVisible] = useState<boolean>(false);
  const { functionality } = useGetSharedFunctionality();

  let baseMenuItems = baseLinks;

  if (!functionality?.is_shared_spending_limit_enabled) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Customers',
    );
  }

  if (functionality?.is_shared_spending_limit_enabled) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Spending Limits',
    );
  }

  if (spendingLimitRequests.length === 0) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Spending Limits',
    );
  }

  if (!functionality?.can_access_seller_fee_dashboard) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Fees',
    );
  }

  if (isContributor) {
    baseMenuItems = baseMenuItems.filter(
      (menuItem) => menuItem.title !== 'Integrations',
    );
  }

  let secondaryMenuItems = secondaryLinks;
  if (!isOnboarded) secondaryMenuItems = [];

  const LoggedInItems = () => {
    return (
      <nav className={styles.navBarDrawer} aria-label="Main Menu">
        <div>
          <SafeLink to={'/pay/dashboard'}>
            <img
              src={iwocaPayLogo}
              className={styles.logo}
              alt="iwocaPay logo"
              aria-hidden="true"
            />
          </SafeLink>
          <div className={styles.userInfo}>
            <NameTag />
          </div>

          {isOnboarded ? (
            <Button
              className={styles.createPayLink}
              onClick={() => {
                navigate({ to: '/pay/pay-links/universal', keepParams: true });
                hideSideBar();
              }}
            >
              Create Pay Link
              <IconPlus className={styles.createPayLinkIcon} />
            </Button>
          ) : (
            <Button
              className={styles.createPayLink}
              onClick={() => {
                navigate({ to: '/pay/activation', keepParams: true });
                hideSideBar();
              }}
            >
              Get started
            </Button>
          )}

          <div className={styles.menuBar}>
            {baseMenuItems.map((menuItem, index) => (
              <MenuItem
                key={index}
                title={menuItem.title}
                internal_path={menuItem.internal_path}
                icon={menuItem.icon}
                onClick={() => hideSideBar()}
                promotional_badge={menuItem.promotional_badge}
              />
            ))}
          </div>
        </div>
        <div>
          <div className={styles.midOptions}>
            {(isStaff || isOwner) && (
              <>
                <MenuItem
                  title="Loans dashboard"
                  icon={{
                    normal: <IconExchange />,
                    selected: <IconExchange />,
                  }}
                  compact={true}
                  onClick={() => {
                    tracking.ampli.switchedToCoreBusinessDashboard();
                    window.location.href = `/account/${buildQueryString()}`;
                  }}
                />
                <hr className={styles.divider} />
              </>
            )}
            {secondaryMenuItems.map((menuItem, index) => (
              <MenuItem
                key={index}
                title={menuItem.title}
                external_link={menuItem.external_link}
                internal_path={menuItem.internal_path}
                onClick={() => hideSideBar()}
                icon={menuItem.icon}
                compact={true}
              />
            ))}
            {(isStaff || isOwner || isAdmin) && (
              <MenuItem
                title="Manage users"
                internal_path="/pay/manage-users"
                icon={{
                  normal: <IconMultipleUsersOutline />,
                  selected: <IconMultipleUsersFilled />,
                }}
                onClick={() => hideSideBar()}
                compact={true}
              />
            )}
            <MenuItem
              title="Manage account"
              internal_path="/pay/manage-account"
              icon={{
                normal: <IconUserCog />,
                selected: <IconUserCog />,
              }}
              onClick={() => hideSideBar()}
              compact={true}
            />
            <hr className={styles.divider} />
            <MenuItem
              title="FAQs"
              external_link={{
                href: 'https://support.iwoca.co.uk/en/collections/2145896-what-s-iwocapay',
                newTab: true,
              }}
              compact={true}
            />
            <MenuItem
              title="Help"
              onClick={() => {
                setHelpModalVisible(true);
              }}
              compact={true}
            />
            <MenuItem
              title="Logout"
              external_link={{ href: '/my_account/logout/', newTab: false }}
              compact={true}
            />
          </div>
        </div>
      </nav>
    );
  };

  const NotLoggedInItems = () => {
    return (
      <nav className={styles.navBarDrawer} aria-label="Main Menu">
        <div>
          <SafeLink to={'/pay/dashboard'}>
            <img
              src={iwocaPayLogo}
              className={styles.logo}
              alt="iwocaPay logo"
              aria-hidden="true"
            />
          </SafeLink>
        </div>
        <div>
          <div className={styles.midOptions}>
            <hr className={styles.divider} />
            <MenuItem
              title="FAQs"
              external_link={{
                href: 'https://support.iwoca.co.uk/en/collections/2145896-what-s-iwocapay',
                newTab: true,
              }}
              compact={true}
            />
            <MenuItem
              title="Help"
              onClick={() => {
                setHelpModalVisible(true);
              }}
              compact={true}
            />
            <MenuItem
              title="Logout"
              external_link={{ href: '/my_account/logout/', newTab: false }}
              compact={true}
            />
          </div>
        </div>
      </nav>
    );
  };

  return (
    <>
      <HelpModal
        isOpen={helpModalVisible}
        closeModal={() => setHelpModalVisible(false)}
      />
      {isLoggedIn ? <LoggedInItems /> : <NotLoggedInItems />}
    </>
  );
};

const MenuItem = ({
  title,
  internal_path,
  external_link,
  onClick,
  compact = false,
  icon = null,
  promotional_badge,
}: {
  title: string;
  internal_path?: ValidRoutePaths;
  external_link?: TExternalLink;
  onClick?: () => void;
  compact?: boolean;
  icon?: TMenuIcon | null;
  promotional_badge?: TMenuLink['promotional_badge'];
}): React.ReactNode => {
  const isSelected: boolean = window.location.pathname.startsWith(
    internal_path as string,
  );

  const PromotionalPill = ({
    promotional_badge,
  }: {
    promotional_badge: TMenuLink['promotional_badge'];
  }) => {
    if (!promotional_badge) return <></>;

    const todayDate = new Date();
    const expiryDate = new Date(promotional_badge.launch_date);
    expiryDate.setDate(
      promotional_badge.launch_date.getDate() + promotional_badge.expiry_days,
    );

    if (todayDate < promotional_badge?.launch_date) return <></>;
    if (todayDate >= expiryDate) return <></>;

    return (
      <Pill variant="red" size="small">
        New
      </Pill>
    );
  };

  const ButtonComponent = () => {
    return (
      <div
        className={classnames(styles.menuBarItem, {
          [styles.menuBarItemSelected]: isSelected,
          [styles.menuBarItemCompact]: compact,
        })}
      >
        {icon && (isSelected ? icon.selected : icon.normal)}
        {title}
        <PromotionalPill promotional_badge={promotional_badge} />
      </div>
    );
  };

  if (external_link) {
    return (
      <a href={external_link.href} target="_blank" rel="noopener noreferrer">
        <ButtonComponent />
      </a>
    );
  }

  if (internal_path && onClick) {
    return (
      <SafeLink onClick={onClick} to={internal_path}>
        <ButtonComponent />
      </SafeLink>
    );
  }

  if (internal_path) {
    return (
      <SafeLink to={internal_path}>
        <ButtonComponent />
      </SafeLink>
    );
  }

  if (onClick) {
    return (
      <button onClick={onClick} className={styles.noStylingButton}>
        <ButtonComponent />
      </button>
    );
  }

  return <ButtonComponent />;
};
